import { format, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
// @mui
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { AccessTime, Schedule } from '@mui/icons-material';
import { TIMEZONES } from './MeetingTimezoneSelector';

export default function MeetingTimeSlots({ 
  date,
  timeSlots = [], 
  selectedTime, 
  onTimeSelect,
  userTimezone 
}) {
  const theme = useTheme();

  if (!date || timeSlots.length === 0) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Schedule sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          날짜를 선택하면 예약 가능한 시간이 표시됩니다.
        </Typography>
      </Paper>
    );
  }

  // 시간대별로 그룹화 (오전/오후)
  const morningSlots = timeSlots.filter(slot => {
    const hour = parseInt(slot.localTime.split(':')[0]);
    return hour < 12;
  });

  const afternoonSlots = timeSlots.filter(slot => {
    const hour = parseInt(slot.localTime.split(':')[0]);
    return hour >= 12 && hour < 18;
  });

  const eveningSlots = timeSlots.filter(slot => {
    const hour = parseInt(slot.localTime.split(':')[0]);
    return hour >= 18;
  });

  const TimeSlotButton = ({ slot }) => {
    const isSelected = selectedTime === slot.originalTime;

    return (
      <Button
        fullWidth
        variant={isSelected ? "contained" : "outlined"}
        onClick={() => onTimeSelect(slot.originalTime)}
        sx={{
          py: 1.5,
          px: 1,
          borderColor: theme.palette.divider,
          color: isSelected ? theme.palette.common.white : theme.palette.text.primary,
          bgcolor: isSelected ? theme.palette.info.main : 'transparent',
          '&:hover': {
            borderColor: theme.palette.info.main,
            bgcolor: isSelected ? theme.palette.info.main : alpha(theme.palette.info.main, 0.04),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTime sx={{ fontSize: 16 }} />
          <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
            {slot.localTime}
          </Typography>
        </Box>
      </Button>
    );
  };

  const TimeSlotSection = ({ title, slots }) => {
    if (slots.length === 0) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 1.5,
            fontWeight: 600,
            color: theme.palette.text.secondary,
          }}
        >
          {title}
        </Typography>
        <Grid container spacing={1}>
          {slots.map((slot, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <TimeSlotButton slot={slot} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const dateObj = parseISO(date);
  const formattedDate = format(dateObj, 'M월 d일 (EEEE)', { locale: ko });

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: { xs: 2, sm: 3 },
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      {/* 헤더 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {formattedDate}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={`${timeSlots.length}개 시간 가능`}
            color="info"
            variant="outlined"
          />
          {userTimezone && (
            <Typography variant="caption" color="text.secondary">
              {TIMEZONES.find(v => v.value == userTimezone).label} 시간대 기준
            </Typography>
          )}
        </Box>
      </Box>

      {/* 시간 슬롯 섹션 */}
      <Box>
        <TimeSlotSection title="오전" slots={morningSlots} />
        <TimeSlotSection title="오후" slots={afternoonSlots} />
        <TimeSlotSection title="저녁" slots={eveningSlots} />
      </Box>

    </Paper>
  );
}